import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FileText } from 'lucide-react';

interface DocumentInputProps {
  value?: string; // digits or formatted; onChange returns digits only
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
  label?: string;
  helpText?: string;
  error?: string;
  onBlur?: () => void;
  /** Restrict masking/validation to a single document kind. Omit for auto (CPF/CNPJ by length). */
  documentType?: 'CPF' | 'CNPJ';
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Req = styled.span`
  color: ${(p) => p.theme.error};
  margin-left: 4px;
`;

const InputWrapper = styled.div<{ $disabled?: boolean; $hasError?: boolean }>`
  display: flex;
  align-items: center;
  background: ${(p) => (p.$disabled ? p.theme.surfaceAlt : p.theme.surface)};
  border: 1px solid ${(p) => (p.$hasError ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  box-sizing: border-box;
  min-height: 42px;
  padding: 0 0.75rem;
  transition: all 0.2s;

  &:focus-within {
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }

  .icon {
    display: inline-flex;
    align-items: center;
    color: ${(p) => p.theme.textMuted};
    margin-right: 0.5rem;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  padding: 0.6rem 0;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  color: ${(p) => p.theme.textPrimary};
  outline: none;

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const HelpText = styled.small<{ $isError?: boolean }>`
  font-size: 0.75rem;
  color: ${(p) => (p.$isError ? p.theme.error : p.theme.textMuted)};
`;

/** Keep only digits, capped at 14 (CPF has 11, CNPJ has 14). */
const onlyDigits = (text: string): string => (text || '').replace(/\D/g, '').slice(0, 14);

export const maskCpf = (d: string): string => {
  let r = d.slice(0, 3);
  if (d.length > 3) r += `.${d.slice(3, 6)}`;
  if (d.length > 6) r += `.${d.slice(6, 9)}`;
  if (d.length > 9) r += `-${d.slice(9, 11)}`;
  return r;
};

export const maskCnpj = (d: string): string => {
  let r = d.slice(0, 2);
  if (d.length > 2) r += `.${d.slice(2, 5)}`;
  if (d.length > 5) r += `.${d.slice(5, 8)}`;
  if (d.length > 8) r += `/${d.slice(8, 12)}`;
  if (d.length > 12) r += `-${d.slice(12, 14)}`;
  return r;
};

/** Incremental CPF/CNPJ mask (auto-switches by length). */
export const maskDocument = (digits: string): string => {
  const d = onlyDigits(digits);
  if (d.length === 0) return '';
  return d.length <= 11 ? maskCpf(d) : maskCnpj(d);
};

/** Format a document for display; returns the original if it has no digits. */
export const formatDocument = (doc?: string | null): string => maskDocument(doc || '') || (doc || '');

/** Format a CPF for display (always CPF mask, capped at 11 digits). */
export const formatCpf = (doc?: string | null): string =>
  maskCpf((doc || '').replace(/\D/g, '').slice(0, 11)) || (doc || '');

/**
 * Standard CPF/CNPJ field (pt-BR): document icon + incremental masking.
 * `onChange` returns digits only (e.g. "11111111111111").
 */
export const DocumentInput: React.FC<DocumentInputProps> = ({
  value,
  onChange,
  placeholder = 'CPF ou CNPJ',
  disabled,
  required,
  className,
  id,
  name,
  label,
  helpText,
  error,
  onBlur,
  documentType,
}) => {
  const [display, setDisplay] = useState<string>('');

  const maxDigits = documentType === 'CPF' ? 11 : 14;
  const applyMask = (digits: string): string =>
    documentType === 'CPF' ? maskCpf(digits) : documentType === 'CNPJ' ? maskCnpj(digits) : maskDocument(digits);
  const normalize = (raw: string): string => (raw || '').replace(/\D/g, '').slice(0, maxDigits);

  useEffect(() => {
    setDisplay(applyMask(normalize(value || '')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, documentType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = normalize(e.target.value);
    setDisplay(applyMask(digits));
    onChange(digits);
  };

  return (
    <Container className={className}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <Req>*</Req>}
        </Label>
      )}
      <InputWrapper $disabled={disabled} $hasError={!!error}>
        <span className="icon" aria-hidden="true">
          <FileText size={16} />
        </span>
        <StyledInput
          id={id}
          name={name}
          value={display}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          inputMode="numeric"
        />
      </InputWrapper>
      {(helpText || error) && <HelpText $isError={!!error}>{error || helpText}</HelpText>}
    </Container>
  );
};

export default DocumentInput;
